/**
 * Public invite acceptance: validate token, then register or log in and accept.
 * Route: /invite/accept?token=... (no AuthGuard so email link works).
 */
import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import PasswordRequirementsHint from '@/components/PasswordRequirementsHint'

interface ValidateResult {
  valid: boolean
  reason?: string
  email?: string
  email_mask?: string
  target_role_label?: string
  target_user_type?: string
}

export default function InviteAcceptPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const navigate = useNavigate()
  const { user, session } = useAuth()
  const [mode, setMode] = useState<'choose' | 'register' | 'login'>('choose')
  const [password, setPassword] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [acceptSuccess, setAcceptSuccess] = useState(false)

  const { data: validation, isLoading: validating, isError: validationError, error: validationErrorDetail } = useQuery({
    queryKey: ['validate-invite-token', token],
    queryFn: async (): Promise<ValidateResult> => {
      const { data, error } = await supabase.rpc('validate_invite_token', { p_token: token })
      if (error) throw error
      return (data ?? { valid: false }) as ValidateResult
    },
    enabled: !!token.trim(),
    staleTime: 60_000,
  })

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('accept_invite', { p_token: token })
      if (error) throw error
      return data as { success?: boolean; error?: string; already_accepted?: boolean; already_higher?: boolean }
    },
    onSuccess: (result) => {
      if (result?.success) {
        setAcceptSuccess(true)
        setTimeout(() => navigate('/', { replace: true }), 2000)
      } else {
        setError(result?.error ?? 'Accept failed')
      }
    },
    onError: (e) => {
      setError(e instanceof Error ? e.message : 'Failed to accept invite')
    },
  })

  // If user is already logged in and token is valid, accept immediately (or after they landed from login redirect)
  useEffect(() => {
    if (!user || !session || !validation?.valid || acceptMutation.isPending || acceptSuccess) return
    acceptMutation.mutate()
  }, [user, session, validation?.valid])

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validation?.valid || !validation.email) {
      setError(validationError ? 'Invite link could not be verified. The invite feature may not be set up on this server (see console).' : 'Invite link is invalid or expired. Please use the link from your email.')
      return
    }
    setError(null)
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (anonKey) {
      headers.apikey = anonKey
      headers.Authorization = `Bearer ${anonKey}`
    }
    const signupRes = await fetch(`${supabaseUrl}/functions/v1/invite-signup`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        email: validation.email,
        token,
        password,
      }),
    })
    const signupResult = (await signupRes.json().catch(() => ({}))) as { success?: boolean; error?: string; message?: string }
    if (!signupResult.success) {
      if (signupResult.error === 'already_exists') {
        setError(signupResult.message ?? 'This email already has an account. Use the login link below.')
        return
      }
      setError(signupResult.error ?? signupResult.message ?? 'Could not create account.')
      return
    }
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: validation.email,
      password,
    })
    if (signInError) {
      setError(signInError.message)
      return
    }
    acceptMutation.mutate()
  }

  if (!token.trim()) {
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">Invalid invite link</h1>
        <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
          This invite link is missing the token. Please use the link from your invite email.
        </p>
        <Link to="/login" className="text-text-link underline font-semibold">Go to login</Link>
      </div>
    )
  }

  if (validating || (validation && !validation.valid && !validation.reason)) {
    return (
      <div className="max-w-md mx-auto">
        <p style={{ color: 'var(--text-secondary)' }}>Checking invite link…</p>
      </div>
    )
  }

  if (validationError) {
    const message = validationErrorDetail instanceof Error ? validationErrorDetail.message : 'Could not verify invite link.'
    const is404 = typeof message === 'string' && (message.includes('404') || message.includes('does not exist'))
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">Invite link could not be verified</h1>
        <p className="mb-4" style={{ color: 'var(--text-primary)' }}>{message}</p>
        {is404 && (
          <p className="mb-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
            The invite feature may not be set up on this environment. Ensure migrations 065, 066 and 067 (invites, RPCs) are applied to your Supabase project.
          </p>
        )}
        <Link to="/login" className="text-text-link underline font-semibold">Go to login</Link>
      </div>
    )
  }

  if (validation && !validation.valid) {
    const reason = validation.reason ?? 'invalid'
    const messages: Record<string, string> = {
      expired: 'This invite has expired.',
      cancelled: 'This invite was cancelled.',
      already_used: 'This invite has already been used.',
      invalid_token: 'This invite link is invalid or has been used.',
      missing_token: 'This invite link is invalid.',
    }
    const message = messages[reason] ?? 'This invite link is not valid.'
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">Invite not valid</h1>
        <p className="mb-4" style={{ color: 'var(--text-primary)' }}>{message}</p>
        <Link to="/login" className="text-text-link underline font-semibold">Go to login</Link>
      </div>
    )
  }

  if (acceptSuccess || (user && validation?.valid && acceptMutation.isSuccess)) {
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">You’re in</h1>
        <p className="mb-4" style={{ color: 'var(--text-primary)' }}>
          Your account is set up. Redirecting you to the home page…
        </p>
      </div>
    )
  }

  if (user && validation?.valid && !acceptMutation.isSuccess && !acceptMutation.isError) {
    return (
      <div className="max-w-md mx-auto">
        <p style={{ color: 'var(--text-secondary)' }}>Accepting invite…</p>
      </div>
    )
  }

  if (user && validation?.valid && acceptMutation.isError) {
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">Something went wrong</h1>
        <p className="mb-4 text-color-error" role="alert">{error ?? 'Failed to accept invite.'}</p>
        <button
          type="button"
          className="btn-primary"
          onClick={() => acceptMutation.mutate()}
        >
          Try again
        </button>
      </div>
    )
  }

  const inviteEmail = validation?.email ?? ''
  const roleLabel = validation?.target_role_label ?? 'member'

  const loginRedirect = `/invite/accept?token=${encodeURIComponent(token)}`

  if (mode === 'login') {
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">Log in to accept your invite</h1>
        <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
          This invite is for <strong>{inviteEmail}</strong>. Log in with that email to accept and get your {roleLabel} role.
        </p>
        <Link
          to={`/login?redirect=${encodeURIComponent(loginRedirect)}&email=${encodeURIComponent(inviteEmail)}&invite_token=${encodeURIComponent(token)}`}
          className="inline-block px-4 py-2 rounded font-medium"
          style={{
            backgroundColor: 'var(--bg-interactive)',
            color: 'var(--text-inverse)',
          }}
        >
          Go to login
        </Link>
        <button
          type="button"
          className="ml-3 text-text-link underline"
          onClick={() => setMode('choose')}
        >
          Back
        </button>
      </div>
    )
  }

  if (mode === 'register') {
    return (
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl font-bold mb-6">Create your account</h1>
        <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
          You’ve been invited to join as a <strong>{roleLabel}</strong>. Create a password to finish.
        </p>
        <form onSubmit={handleRegister} className="space-y-4">
          <div>
            <label htmlFor="invite-email" className="block mb-2 font-semibold">Email</label>
            <input
              id="invite-email"
              type="email"
              value={inviteEmail}
              readOnly
              className="w-full opacity-90"
              aria-describedby="invite-email-note"
            />
            <p id="invite-email-note" className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
              This email is set by your invite.
            </p>
          </div>
          <div>
            <label htmlFor="invite-password" className="block mb-2 font-semibold">
              Password <span className="text-color-error" aria-label="required">*</span>
            </label>
            <div className="flex gap-2">
              <input
                id="invite-password"
                type={passwordVisible ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="w-full flex-1"
                autoComplete="new-password"
                aria-describedby="invite-password-hint"
              />
              <button
                type="button"
                onClick={() => setPasswordVisible((v) => !v)}
                className="px-3 py-2 rounded border shrink-0"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                }}
                aria-label={passwordVisible ? 'Hide password' : 'Show password'}
              >
                {passwordVisible ? 'Hide' : 'Show'}
              </button>
            </div>
            <PasswordRequirementsHint id="invite-password-hint" />
          </div>
          {error && (
            <p className="text-color-error" role="alert">{error}</p>
          )}
          <button
            type="submit"
            disabled={acceptMutation.isPending}
            className="w-full px-4 py-3 rounded font-medium"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
          >
            {acceptMutation.isPending ? 'Creating account…' : 'Create account'}
          </button>
          <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
            You’ll be signed in and sent to the homepage. If we need to verify your email first, you’ll be taken to the login page with your email filled in.
          </p>
        </form>
        <p className="mt-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Already have an account?{' '}
          <button type="button" className="text-text-link underline" onClick={() => setMode('login')}>
            Log in
          </button>
          {' — or '}
          <button type="button" className="text-text-link underline" onClick={() => setMode('choose')}>
            back to choices
          </button>
        </p>
      </div>
    )
  }

  return (
    <div className="max-w-md mx-auto">
      <h1 className="text-2xl font-bold mb-6">You’re invited</h1>
      <p className="mb-4" style={{ color: 'var(--text-primary)' }}>
        You’ve been invited to join Inside Access as a <strong>{roleLabel}</strong>.
      </p>
      <p className="mb-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
        Invite for: <strong>{validation?.email_mask ?? inviteEmail}</strong>
      </p>
      <div className="flex flex-col gap-3">
        <button
          type="button"
          className="w-full px-4 py-3 rounded font-medium"
          style={{
            backgroundColor: 'var(--bg-interactive)',
            color: 'var(--text-inverse)',
          }}
          onClick={() => setMode('register')}
        >
          I’m new — create my account
        </button>
        <button
          type="button"
          className="w-full px-4 py-3 rounded border-2"
          style={{
            borderColor: 'var(--bg-tertiary)',
            backgroundColor: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
          }}
          onClick={() => setMode('login')}
        >
          I already have an account — log in
        </button>
      </div>
    </div>
  )
}
